import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Brain, Activity, Eye, Zap, Waves, Loader2, TrendingUp } from "lucide-react";
import { ACCENT } from "@/components/neurology/shared";
import OverviewTab from "@/components/neurology/OverviewTab";
import NihssTab from "@/components/neurology/NihssTab";
import GcsTab from "@/components/neurology/GcsTab";
import MigraineTab from "@/components/neurology/MigraineTab";
import SeizureTab from "@/components/neurology/SeizureTab";
import MrsTab from "@/components/neurology/MrsTab";

/* ------------------------------------------------------------------ */
/* Neurology dashboard — 5 tabs                                       */
/*   1. Overview   — latest NIHSS + GCS + migraine + seizure at-a-glance
     2. NIHSS      — 15-item stroke scale scoring + history + trend
     3. GCS        — Glasgow Coma Scale scoring + history
     4. Migraine   — episode diary + 30-day trend
     5. Epilepsy   — seizure diary + control-band summary                */
/* ------------------------------------------------------------------ */

export default function Neurology() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "overview");
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) setTabState(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [nihss, setNihss] = useState({ records: [] });
  const [gcs, setGcs] = useState({ records: [] });
  const [migraine, setMigraine] = useState({ episodes: [], summary: {} });
  const [seizure, setSeizure] = useState({ episodes: [], control: null, summary: {} });
  const [mrs, setMrs] = useState({ records: [] });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [n, g, m, s, r] = await Promise.all([
        api.get("/specialties/neurology/nihss"),
        api.get("/specialties/neurology/gcs"),
        api.get("/specialties/neurology/migraine"),
        api.get("/specialties/neurology/seizure"),
        api.get("/specialties/neurology/mrs"),
      ]);
      setNihss(n.data);
      setGcs(g.data);
      setMigraine(m.data);
      setSeizure(s.data);
      setMrs(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load neurology data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const TABS = [
    { key: "overview", label: "Overview", icon: Brain },
    { key: "nihss",    label: "NIHSS",    icon: Activity },
    { key: "gcs",      label: "GCS",      icon: Eye },
    { key: "mrs",      label: "mRS",      icon: TrendingUp },
    { key: "migraine", label: "Migraine", icon: Zap },
    { key: "seizure",  label: "Epilepsy", icon: Waves },
  ];

  return (
    <div className="space-y-6" data-testid="neurology-page">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: `${ACCENT}22`, border: `1px solid ${ACCENT}55` }}>
            <Brain size={22} style={{ color: ACCENT }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Neurology console</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              NIH Stroke Scale, Glasgow Coma Scale, migraine and seizure diaries — AHA/ASA + ILAE guidelines.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="neuro-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`neuro-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? "text-black border-transparent"
                  : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white hover:border-[#A78BFA]/40"
              }`}
              style={active ? { background: ACCENT } : {}}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading neurology data…
        </div>
      ) : (
        <>
          {tab === "overview" && (
            <OverviewTab nihss={nihss} gcs={gcs} migraine={migraine} seizure={seizure}
                         onJump={setTab} />
          )}
          {tab === "nihss"    && <NihssTab   data={nihss}    onSaved={loadAll} />}
          {tab === "gcs"      && <GcsTab     data={gcs}      onSaved={loadAll} />}
          {tab === "mrs"      && <MrsTab     data={mrs}      onSaved={loadAll} />}
          {tab === "migraine" && <MigraineTab data={migraine} onSaved={loadAll} />}
          {tab === "seizure"  && <SeizureTab data={seizure}  onSaved={loadAll} />}
        </>
      )}
    </div>
  );
}
